import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { isIOS } from '../lib/platform';

const PlayerContext = createContext(null);

const BASMALLAH_URL = '/audio/basmallah.mp3';

// Basmallah modes: 'first' = first excerpt only, 'all' = every excerpt, 'none' = never
const BASMALLAH_MODES = ['first', 'all', 'none'];

// Playback speed options
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

// iOS Safari ignores HTMLMediaElement.volume writes — users must use hardware buttons.
const IS_IOS = typeof window !== 'undefined' ? isIOS() : false;
const VOLUME_SUPPORTED = !IS_IOS;

export const PlayerProvider = ({ children }) => {
  const [currentExcerpt, setCurrentExcerpt] = useState(null);
  const [currentSurah, setCurrentSurah] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [volume, setVolumeState] = useState(() => {
    if (typeof window === 'undefined') return 0.8;
    const stored = parseFloat(localStorage.getItem('quran-player-volume'));
    return Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : 0.8;
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isPlayingBasmallah, setIsPlayingBasmallah] = useState(false);
  const [basmallahMode, setBasmallahMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('quran-player-basmallah-mode') || 'first';
    }
    return 'first';
  });
  const [playbackSpeed, setPlaybackSpeedState] = useState(() => {
    if (typeof window !== 'undefined') {
      return parseFloat(localStorage.getItem('quran-player-speed')) || 1;
    }
    return 1;
  });
  
  // Repeat settings
  const [repeatOptions, setRepeatOptionsState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('quran-player-repeat-options');
      return saved ? JSON.parse(saved) : [1, 10, 50, 100, -1]; // -1 = infinite
    }
    return [1, 10, 50, 100, -1];
  });
  const [repeatCount, setRepeatCount] = useState(0); // 0 = off, -1 = infinite, >0 = specific count
  const [currentRepeatIndex, setCurrentRepeatIndex] = useState(0); // tracks how many times played

  const audioRef = useRef(null);
  const basmallahRef = useRef(null);
  const excerptStartTime = useRef(0);
  const excerptEndTime = useRef(0);
  const lastSurahIdRef = useRef(null);
  const isTransitioning = useRef(false);
  const repeatCountRef = useRef(0);

  // Store current state in refs for use in callbacks
  const stateRef = useRef({
    playlist: [],
    currentExcerpt: null,
    currentSurah: null,
    isShuffle: false,
    repeatCount: 0,
    basmallahMode: 'first'
  });

  // Keep stateRef in sync
  useEffect(() => {
    stateRef.current = {
      playlist,
      currentExcerpt,
      currentSurah,
      isShuffle,
      repeatCount,
      basmallahMode
    };
    repeatCountRef.current = repeatCount;
  }, [playlist, currentExcerpt, currentSurah, isShuffle, repeatCount, basmallahMode]);

  // Save repeat options to localStorage
  const setRepeatOptions = useCallback((options) => {
    setRepeatOptionsState(options);
    localStorage.setItem('quran-player-repeat-options', JSON.stringify(options));
  }, []);

  // Cycle through repeat options
  const cycleRepeat = useCallback(() => {
    if (repeatOptions.length === 0) {
      setRepeatCount(0);
      return;
    }
    
    const currentIndex = repeatOptions.indexOf(repeatCount);
    if (currentIndex === -1 || currentIndex === repeatOptions.length - 1) {
      // Not found or last option, go back to off (0)
      setRepeatCount(0);
      setCurrentRepeatIndex(0);
    } else {
      // Move to next option
      setRepeatCount(repeatOptions[currentIndex + 1]);
      setCurrentRepeatIndex(0);
    }
  }, [repeatOptions, repeatCount]);

  // Save basmallah mode to localStorage
  useEffect(() => {
    localStorage.setItem('quran-player-basmallah-mode', basmallahMode);
  }, [basmallahMode]);

  // Initialize audio elements once
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.playsInline = true;
      audioRef.current.preload = 'auto';
    }
    if (!basmallahRef.current) {
      basmallahRef.current = new Audio(BASMALLAH_URL);
      basmallahRef.current.playsInline = true;
      basmallahRef.current.preload = 'auto';
    }
  }, []);

  // Update volume (no-op on iOS — the platform ignores volume writes).
  // Persist user preference for non-iOS platforms.
  const setVolume = useCallback((v) => {
    const clamped = Math.max(0, Math.min(1, Number(v) || 0));
    setVolumeState(clamped);
    if (typeof window !== 'undefined' && VOLUME_SUPPORTED) {
      try { localStorage.setItem('quran-player-volume', String(clamped)); } catch (e) { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (!VOLUME_SUPPORTED) return;
    if (audioRef.current) audioRef.current.volume = volume;
    if (basmallahRef.current) basmallahRef.current.volume = volume;
  }, [volume]);

  // Update playback speed
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackSpeed;
    if (basmallahRef.current) basmallahRef.current.playbackRate = playbackSpeed;
    localStorage.setItem('quran-player-speed', playbackSpeed.toString());
  }, [playbackSpeed]);

  // Set playback speed with validation
  const setPlaybackSpeed = useCallback((speed) => {
    const validSpeed = PLAYBACK_SPEEDS.includes(speed) ? speed : 1;
    setPlaybackSpeedState(validSpeed);
  }, []);

  // Play a specific excerpt
  const playExcerptDirect = useCallback((excerpt, surah, excerptList, skipBasmallah = false) => {
    if (!excerpt?.audio_file_id || !audioRef.current) return;

    console.log('Playing excerpt:', excerpt.page_number, 'of', surah?.name_french);

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const audioUrl = `${API_URL}/api/audio/${excerpt.audio_file_id}`;

    // Reset transition flag and repeat index
    isTransitioning.current = false;
    setCurrentRepeatIndex(0);

    // Stop all current audio
    audioRef.current.pause();
    if (basmallahRef.current) {
      basmallahRef.current.pause();
      basmallahRef.current.currentTime = 0;
    }
    setIsPlayingBasmallah(false);

    // Set excerpt timing
    excerptStartTime.current = excerpt.start_time || 0;
    excerptEndTime.current = excerpt.end_time || 0;
    
    const excerptDuration = excerpt.duration || (excerptEndTime.current - excerptStartTime.current);
    setDuration(excerptDuration > 0 ? excerptDuration : 0);
    setCurrentTime(0);

    // Update state
    setCurrentExcerpt(excerpt);
    setCurrentSurah(surah);
    setPlaylist(excerptList);

    // Check if should play basmallah based on mode
    const currentMode = stateRef.current.basmallahMode;
    const isNewSurah = surah?.id !== lastSurahIdRef.current;
    const isFirstExcerpt = excerptList.length > 0 && excerptList[0]?.id === excerpt.id;
    
    let shouldPlayBasmallah = false;
    if (!skipBasmallah && currentMode !== 'none') {
      if (currentMode === 'all') {
        // Play before every excerpt
        shouldPlayBasmallah = true;
      } else if (currentMode === 'first') {
        // Play only before first excerpt of a new surah
        shouldPlayBasmallah = isNewSurah && isFirstExcerpt;
      }
    }

    lastSurahIdRef.current = surah?.id;

    // Load new audio
    audioRef.current.src = audioUrl;
    audioRef.current.load();

    let hasStarted = false;
    
    const startMainAudio = () => {
      if (hasStarted || !audioRef.current) return;
      hasStarted = true;
      
      audioRef.current.currentTime = excerptStartTime.current;
      setIsPlaying(true);
      audioRef.current.play().catch(err => {
        console.error('Play error:', err);
        setIsPlaying(false);
      });
    };

    if (shouldPlayBasmallah && basmallahRef.current) {
      setIsPlayingBasmallah(true);
      setIsPlaying(true);
      basmallahRef.current.currentTime = 0;
      basmallahRef.current.play().catch((err) => {
        console.error('Basmallah error:', err);
        setIsPlayingBasmallah(false);
        startMainAudio();
      });
    } else {
      const handleCanPlay = () => {
        audioRef.current.removeEventListener('canplay', handleCanPlay);
        audioRef.current.removeEventListener('error', handleError);
        startMainAudio();
      };
      
      const handleError = (e) => {
        console.error('Audio load error:', e);
        audioRef.current.removeEventListener('canplay', handleCanPlay);
        audioRef.current.removeEventListener('error', handleError);
        setIsPlaying(false);
      };
      
      if (audioRef.current.readyState >= 2) {
        startMainAudio();
      } else {
        audioRef.current.addEventListener('canplay', handleCanPlay);
        audioRef.current.addEventListener('error', handleError);
      }
    }
  }, []);

  // Go to next excerpt
  const goToNextExcerpt = useCallback(() => {
    const { playlist: pl, currentExcerpt: ce, currentSurah: cs, isShuffle: shuffle, basmallahMode } = stateRef.current;

    if (!ce || pl.length === 0) {
      setIsPlaying(false);
      return;
    }

    const currentIndex = pl.findIndex(e => e.id === ce.id);
    console.log('Current index:', currentIndex, 'Playlist length:', pl.length);

    let nextIndex;
    if (shuffle) {
      do {
        nextIndex = Math.floor(Math.random() * pl.length);
      } while (nextIndex === currentIndex && pl.length > 1);
    } else {
      nextIndex = currentIndex + 1;
    }

    if (nextIndex < pl.length) {
      const nextExcerpt = pl[nextIndex];
      console.log('Going to next:', nextExcerpt.page_number);
      // Skip basmallah only if mode is 'first' or 'none', respect 'all' mode
      const skipBasmallah = basmallahMode !== 'all';
      playExcerptDirect(nextExcerpt, cs, pl, skipBasmallah);
    } else {
      console.log('End of playlist');
      setIsPlaying(false);
    }
  }, [playExcerptDirect]);

  // Setup audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    const basmallah = basmallahRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      if (isPlayingBasmallah) return;

      const relTime = audio.currentTime - excerptStartTime.current;
      setCurrentTime(Math.max(0, relTime));
      
      // Check if reached end
      const endTime = excerptEndTime.current;
      const audioDuration = audio.duration;
      
      // Use end_time if set, otherwise use audio duration
      const effectiveEndTime = endTime > 0 ? endTime : audioDuration;
      
      if (effectiveEndTime > 0 && audio.currentTime >= effectiveEndTime - 0.1 && !isTransitioning.current) {
        isTransitioning.current = true;
        audio.pause();
        
        const currentRepeat = repeatCountRef.current;
        console.log('End reached. Repeat count:', currentRepeat);
        
        if (currentRepeat === -1) {
          // Infinite loop: restart current excerpt
          console.log('Infinite loop - restarting');
          isTransitioning.current = false;
          audio.currentTime = excerptStartTime.current;
          audio.play().catch(console.error);
        } else if (currentRepeat > 0) {
          // Counted repeat
          setCurrentRepeatIndex(prev => {
            const newIndex = prev + 1;
            console.log('Repeat index:', newIndex, '/', currentRepeat);
            if (newIndex < currentRepeat) {
              // Still have repeats left
              console.log('Repeating again');
              isTransitioning.current = false;
              audio.currentTime = excerptStartTime.current;
              audio.play().catch(console.error);
              return newIndex;
            } else {
              // Finished all repeats, go to next
              console.log('All repeats done, going to next');
              setTimeout(() => {
                goToNextExcerpt();
              }, 100);
              return 0;
            }
          });
        } else {
          // No repeat, go to next
          console.log('No repeat, going to next');
          setTimeout(() => {
            goToNextExcerpt();
          }, 100);
        }
      }
    };

    const handleLoadedMetadata = () => {
      const excerptDuration = excerptEndTime.current - excerptStartTime.current;
      if (excerptDuration > 0) {
        setDuration(excerptDuration);
      } else {
        setDuration(audio.duration);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      if (!isTransitioning.current) {
        setIsPlaying(false);
      }
    };

    const handleEnded = () => {
      // Audio reached natural end
      if (isTransitioning.current) return;
      
      isTransitioning.current = true;
      const currentRepeat = repeatCountRef.current;
      console.log('Audio ended. Repeat count:', currentRepeat);
      
      if (currentRepeat === -1) {
        // Infinite loop: restart current excerpt
        console.log('Infinite loop - restarting from ended');
        isTransitioning.current = false;
        audio.currentTime = excerptStartTime.current;
        audio.play().catch(console.error);
      } else if (currentRepeat > 0) {
        // Counted repeat
        setCurrentRepeatIndex(prev => {
          const newIndex = prev + 1;
          console.log('Repeat index from ended:', newIndex, '/', currentRepeat);
          if (newIndex < currentRepeat) {
            // Still have repeats left
            isTransitioning.current = false;
            audio.currentTime = excerptStartTime.current;
            audio.play().catch(console.error);
            return newIndex;
          } else {
            // Finished all repeats, go to next
            setTimeout(() => {
              goToNextExcerpt();
            }, 100);
            return 0;
          }
        });
      } else {
        // No repeat, go to next
        goToNextExcerpt();
      }
    };

    const handleBasmallahEnded = () => {
      setIsPlayingBasmallah(false);
      if (audioRef.current) {
        audioRef.current.currentTime = excerptStartTime.current;
        audioRef.current.play().catch(console.error);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    
    // Error handling for mobile
    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
      setIsPlayingBasmallah(false);
    };
    
    audio.addEventListener('error', handleError);
    
    if (basmallah) {
      basmallah.addEventListener('ended', handleBasmallahEnded);
      basmallah.addEventListener('error', handleError);
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      if (basmallah) {
        basmallah.removeEventListener('ended', handleBasmallahEnded);
        basmallah.removeEventListener('error', handleError);
      }
    };
  }, [goToNextExcerpt, isPlayingBasmallah]);

  // Public API
  const playExcerpt = useCallback((excerpt, surah, excerptList = []) => {
    playExcerptDirect(excerpt, surah, excerptList, false);
  }, [playExcerptDirect]);

  const togglePlay = useCallback(() => {
    if (isPlayingBasmallah && basmallahRef.current) {
      if (isPlaying) {
        basmallahRef.current.pause();
        setIsPlaying(false);
      } else {
        basmallahRef.current.play().catch(console.error);
        setIsPlaying(true);
      }
      return;
    }

    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const endTime = excerptEndTime.current;
      if (endTime > 0 && audioRef.current.currentTime >= endTime - 0.1) {
        audioRef.current.currentTime = excerptStartTime.current;
      }
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  }, [isPlaying, isPlayingBasmallah]);

  const seek = useCallback((time) => {
    if (audioRef.current && !isPlayingBasmallah) {
      const absoluteTime = excerptStartTime.current + time;
      audioRef.current.currentTime = absoluteTime;
      setCurrentTime(time);
    }
  }, [isPlayingBasmallah]);

  const playNext = useCallback(() => {
    goToNextExcerpt();
  }, [goToNextExcerpt]);

  const playPrevious = useCallback(() => {
    const { playlist: pl, currentExcerpt: ce, currentSurah: cs } = stateRef.current;

    if (!ce || pl.length === 0) return;

    const currentIndex = pl.findIndex(e => e.id === ce.id);
    const prevIndex = currentIndex <= 0 ? pl.length - 1 : currentIndex - 1;

    const prevExcerpt = pl[prevIndex];
    if (prevExcerpt) {
      playExcerptDirect(prevExcerpt, cs, pl, true);
    }
  }, [playExcerptDirect]);

  const stop = useCallback(() => {
    isTransitioning.current = false;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (basmallahRef.current) {
      basmallahRef.current.pause();
      basmallahRef.current.currentTime = 0;
    }
    setCurrentExcerpt(null);
    setCurrentSurah(null);
    setIsPlaying(false);
    setIsPlayingBasmallah(false);
    lastSurahIdRef.current = null;
  }, []);

  // Keyboard shortcuts (desktop only)
  useEffect(() => {
    // Skip keyboard shortcuts on mobile/touch devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || ('ontouchstart' in window) 
      || (navigator.maxTouchPoints > 0);
    
    if (isMobile) return;

    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      // Only handle shortcuts when there's an active excerpt
      if (!currentExcerpt) return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          // Rewind 5 seconds
          if (audioRef.current && !isPlayingBasmallah) {
            const newTime = Math.max(0, audioRef.current.currentTime - 5);
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          // Forward 5 seconds
          if (audioRef.current && !isPlayingBasmallah) {
            const newTime = Math.min(duration, audioRef.current.currentTime + 5);
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          // Volume up
          setVolume(Math.min(1, volume + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          // Volume down
          setVolume(Math.max(0, volume - 0.1));
          break;
        case 'KeyN':
          e.preventDefault();
          playNext();
          break;
        case 'KeyP':
          e.preventDefault();
          playPrevious();
          break;
        case 'KeyM':
          e.preventDefault();
          // Toggle mute
          setVolume(volume > 0 ? 0 : 0.8);
          break;
        case 'KeyL':
          e.preventDefault();
          // Cycle repeat
          cycleRepeat();
          break;
        case 'KeyS':
          e.preventDefault();
          // Toggle shuffle
          setIsShuffle(!isShuffle);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentExcerpt, togglePlay, playNext, playPrevious, volume, setVolume, duration, isPlayingBasmallah, cycleRepeat, isShuffle]);

  // Media Session action handlers (lock screen controls)
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    navigator.mediaSession.setActionHandler('play', () => togglePlay());
    navigator.mediaSession.setActionHandler('pause', () => togglePlay());
    navigator.mediaSession.setActionHandler('nexttrack', () => playNext());
    navigator.mediaSession.setActionHandler('previoustrack', () => playPrevious());
    navigator.mediaSession.setActionHandler('seekforward', () => {
      if (audioRef.current) {
        audioRef.current.currentTime = Math.min(audioRef.current.currentTime + 10, duration);
      }
    });
    navigator.mediaSession.setActionHandler('seekbackward', () => {
      if (audioRef.current) {
        audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 10, 0);
      }
    });
  }, [togglePlay, playNext, playPrevious, duration]);

  // Update lock screen metadata with repeat counter
  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentExcerpt || !currentSurah) return;

    const displayTitle = currentExcerpt.title || `صفحة ${currentExcerpt.page_number}`;
    const repeatLabel = repeatCount === 0 ? '' : repeatCount === -1 ? ' ∞' : ` ${currentRepeatIndex + 1}/${repeatCount}`;
    const baseUrl = window.location.origin;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: `${displayTitle}${repeatLabel}`,
      artist: currentSurah.name_arabic,
      album: 'مشروع حفظ الستين',
      artwork: [
        { src: `${baseUrl}/media-artwork.png`, sizes: '1024x1024', type: 'image/png' },
        { src: `${baseUrl}/icon-512.png`, sizes: '512x512', type: 'image/png' },
        { src: `${baseUrl}/icon-192.png`, sizes: '192x192', type: 'image/png' },
      ],
    });
  }, [currentExcerpt, currentSurah, repeatCount, currentRepeatIndex]);

  return (
    <PlayerContext.Provider value={{
      currentExcerpt,
      currentSurah,
      playlist,
      isPlaying,
      isShuffle,
      volume,
      currentTime,
      duration,
      isMinimized,
      isPlayingBasmallah,
      basmallahMode,
      playbackSpeed,
      repeatCount,
      repeatOptions,
      currentRepeatIndex,
      playExcerpt,
      togglePlay,
      seek,
      playNext,
      playPrevious,
      stop,
      setVolume,
      isVolumeSupported: VOLUME_SUPPORTED,
      setIsShuffle,
      setIsMinimized,
      setBasmallahMode,
      setPlaybackSpeed,
      repeatCount,
      repeatOptions,
      currentRepeatIndex,
      setRepeatOptions,
      cycleRepeat,
      setRepeatCount,
    }}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};
